import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Checkbox } from '@mantine/core';
import { type KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Text } from '../../../../ui-library/typography/typography';
import type { DocumentTypeOption } from './agent-document.helpers';

interface DocumentFilterMenuProps {
  options: DocumentTypeOption[];
  selected: string[];
  onToggle: (typeKey: string) => void;
  onClear: () => void;
}

/**
 * Multi-select file-type filter for the Documents page: a "File type" button
 * showing the active count, opening a dropdown of checkbox options (one per
 * file type present) with a Clear action. Closes on outside click. Selection is
 * owned by the parent.
 *
 * @param {DocumentFilterMenuProps} props - Options, current selection, and callbacks.
 * @returns {JSX.Element} The filter button and dropdown.
 */
export function DocumentFilterMenu({ options, selected, onToggle, onClear }: DocumentFilterMenuProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const count = selected.length;
  const hasFilters = count > 0;

  // Close and return focus to the trigger — used by Escape (keyboard users
  // must be able to dismiss without a mouse).
  const closeAndRefocus = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  // Move focus into the dropdown when it opens so Escape and tabbing work.
  useEffect(() => {
    if (open) dropdownRef.current?.focus();
  }, [open]);

  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      closeAndRefocus();
    }
  };

  return (
    <Root onKeyDown={handleKeyDown}>
      <FilterButton
        ref={buttonRef}
        type="button"
        $open={open}
        aria-label="Filter by file type"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <Icon icon="material-symbols:filter-list" width={18} height={18} style={{ color: 'var(--ink-light)' }} />
        <Text size="small" weight="medium" color="var(--ink-dark)">
          File type
        </Text>
        {hasFilters && <CountBadge>{count}</CountBadge>}
        <Chevron
          icon="material-symbols:expand-more"
          width={18}
          height={18}
          style={{ color: 'var(--ink-light)', transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </FilterButton>

      {open && (
        <>
          <Overlay aria-hidden="true" onClick={() => setOpen(false)} />
          <Dropdown ref={dropdownRef} role="group" aria-label="Filter by type" tabIndex={-1}>
            <DropdownHeader>
              <Text size="tiny" weight="bold" color="var(--ink-lighter)" style={{ letterSpacing: '0.04em' }}>
                FILTER BY TYPE
              </Text>
              {hasFilters && (
                <ClearButton type="button" onClick={onClear}>
                  Clear
                </ClearButton>
              )}
            </DropdownHeader>
            {options.map((option) => (
              <OptionRow key={option.key}>
                <Checkbox
                  size="xs"
                  checked={selected.includes(option.key)}
                  onChange={() => onToggle(option.key)}
                  aria-label={option.label}
                  color="var(--secondary-base)"
                  label={
                    <OptionLabel>
                      <ColorDot style={{ background: option.color }} />
                      <Text size="small" color="var(--ink-dark)">
                        {option.label}
                      </Text>
                    </OptionLabel>
                  }
                />
              </OptionRow>
            ))}
          </Dropdown>
        </>
      )}
    </Root>
  );
}

const Root = styled.div`
  position: relative;
`;

const FilterButton = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid ${({ $open }) => ($open ? 'var(--secondary-base)' : 'var(--sky-base)')};
  background: var(--base-white);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s;
  &:hover {
    border-color: var(--secondary-base);
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--secondary-base);
  color: var(--base-white);
  font-size: 11px;
  font-weight: 600;
`;

const Chevron = styled(Icon)`
  transition: transform 0.15s;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 31;
  width: 240px;
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  border-radius: 12px;
  box-shadow: var(--box-shadow-medium-hard);
  padding: 6px;
  display: flex;
  flex-direction: column;
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px 8px;
`;

const ClearButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--secondary-base);
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 8px;
  &:hover {
    background: var(--sky-lightest);
  }
`;

const OptionLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
`;
